import { LitElement, html, css } from '@hh/global.js'

class HHBundleItemsPicker extends LitElement {
  static properties = {}

  constructor() {
    super()

    this.triggers = this.querySelectorAll('.js-bundle-item-line-edit')
    this.overlay = document.querySelector('.js-bundle-selection-overlay')
    this.availabilityInputs = this.querySelectorAll('.js-bundle-item-available')

    this.learnMoreTriggers = document.querySelectorAll(
      'hh-bundle-items-picker .js-css-learn-more, .js-bundle-selection-drawer .js-css-learn-more'
    )
    this.detailPanels = this.querySelectorAll('.js-css-detail-panel')
    this.detailCloseButtons = this.querySelectorAll('.js-css-detail-close')
    this.detailDrawer = this.querySelector('.js-bundle-detail-drawer')
    this.focusAnchor = null
    this.resetPanelsTimeout = null

    this.handleEditClick = this.handleEditClick.bind(this)
    this.handleLearnMoreClick = this.handleLearnMoreClick.bind(this)
    this.closeDetail = this.closeDetail.bind(this)
    this.handleOverlayClick = this.handleOverlayClick.bind(this)
    this.handleKeyup = this.handleKeyup.bind(this)
    this.stopDrawerPropagation = this.stopDrawerPropagation.bind(this)
    this.handleDrawerTransitionEnd = this.handleDrawerTransitionEnd.bind(this)
  }

  connectedCallback() {
    super.connectedCallback()

    this.triggers.forEach(trigger => {
      trigger.addEventListener('click', this.handleEditClick)
    }, false)

    if (this.detailDrawer) {
      this.detailDrawer.addEventListener('click', this.stopDrawerPropagation)
      this.detailDrawer.addEventListener('transitionend', this.handleDrawerTransitionEnd)
      document.body.appendChild(this.detailDrawer)
    }

    this.learnMoreTriggers.forEach(trigger => {
      trigger.addEventListener('click', this.handleLearnMoreClick)
    })
    this.detailCloseButtons.forEach(button => {
      button.addEventListener('click', this.closeDetail)
    })
    this.overlay?.addEventListener('click', this.handleOverlayClick)
    document.addEventListener('keyup', this.handleKeyup)

    this.setAvailability()
  }

  disconnectedCallback() {
    super.disconnectedCallback()

    this.triggers.forEach(trigger => {
      trigger.removeEventListener('click', this.handleEditClick)
    })

    this.learnMoreTriggers.forEach(trigger => {
      trigger.removeEventListener('click', this.handleLearnMoreClick)
    })
    this.detailCloseButtons.forEach(button => {
      button.removeEventListener('click', this.closeDetail)
    })
    clearTimeout(this.resetPanelsTimeout)
    this.detailDrawer?.removeEventListener('click', this.stopDrawerPropagation)
    this.detailDrawer?.removeEventListener('transitionend', this.handleDrawerTransitionEnd)
    this.detailDrawer?.remove()
    this.focusAnchor = null
    this.overlay?.removeEventListener('click', this.handleOverlayClick)
    document.removeEventListener('keyup', this.handleKeyup)
  }

  stopDrawerPropagation(e) {
    e.stopPropagation()
  }

  // activate focus trap only after the transition ends to always trap focus on open drawers
  handleDrawerTransitionEnd(e) {
    if (e.target !== this.detailDrawer) return
    if (!this.detailDrawer.classList.contains('active')) return

    const panel = this.detailDrawer.querySelector('.js-css-detail-panel:not([inert])')
    panel?.closest('hh-focus-trap')?.setAttribute('active', 'true')
    panel?.querySelector('.js-css-detail-close')?.focus({ preventScroll: true })
  }

  lockScroll() {
    if (document.body.classList.contains('no-scroll')) return
    document.body.style.top = `-${window.scrollY}px`
    document.body.classList.add('no-scroll')
  }

  unlockScroll() {
    if (!document.body.classList.contains('no-scroll')) return
    const scrollY = Math.abs(parseInt(document.body.style.top || '0', 10))
    document.body.classList.remove('no-scroll')
    document.body.style.top = ''
    window.scrollTo({ left: 0, top: scrollY, behavior: 'instant' })
  }

  openDrawer(drawer) {
    if (!drawer || !this.overlay) return
    drawer.classList.add('active')
    drawer.removeAttribute('inert')
    this.overlay.classList.add('active')
    this.lockScroll()
  }

  closeDrawer(drawer) {
    if (!drawer?.classList.contains('active')) return
    drawer.classList.remove('active')
    drawer.setAttribute('inert', '')

    // keep the shared overlay and scroll lock if an edit drawer is still open underneath
    if (!document.querySelector('.js-bundle-selection-drawer.active')) {
      this.overlay.classList.remove('active')
      this.unlockScroll()
    }
  }

  resetDetailPanels() {
    this.detailPanels.forEach(panel => {
      panel.classList.add('opacity-0', 'pointer-events-none')
      panel.setAttribute('inert', '')
      panel.closest('hh-focus-trap')?.setAttribute('active', 'false')
      panel.querySelector('.js-css-detail-inner')?.classList.remove('is-entering')
    })
  }

  restoreFocus() {
    if (
      this.focusAnchor &&
      document.contains(this.focusAnchor) &&
      !this.focusAnchor.hasAttribute('disabled')
    ) {
      this.focusAnchor.focus({ preventScroll: true })
    }
    this.focusAnchor = null
  }

  handleLearnMoreClick(e) {
    e.preventDefault()
    e.stopPropagation()

    const id = e.currentTarget.dataset.id
    const target = this.detailDrawer?.querySelector(`.js-css-detail-panel[data-id="${id}"]`)
    if (!target) return

    this.focusAnchor = e.currentTarget

    clearTimeout(this.resetPanelsTimeout)
    this.resetDetailPanels()
    this.openDrawer(this.detailDrawer)

    target.classList.remove('opacity-0', 'pointer-events-none')
    target.removeAttribute('inert')

    requestAnimationFrame(() => {
      target.querySelector('.js-css-detail-inner')?.classList.add('is-entering')
    })
  }

  closeDetail() {
    if (!this.detailDrawer?.classList.contains('active')) return

    this.closeDrawer(this.detailDrawer)
    this.detailDrawer.querySelectorAll('hh-focus-trap[active="true"]').forEach(trap => {
      trap.setAttribute('active', 'false')
    })

    this.resetPanelsTimeout = setTimeout(() => this.resetDetailPanels(), 300)

    this.restoreFocus()
  }

  handleOverlayClick(e) {
    if (!this.detailDrawer?.classList.contains('active')) return
    e.stopPropagation()
    this.closeDetail()
  }

  handleKeyup(e) {
    if (e.key === 'Escape') this.closeDetail()
  }

  handleEditClick(e) {
    e.preventDefault()

    const trigger = e.target?.closest?.('[data-target]') || e.currentTarget
    const targetId = trigger?.dataset.target || ''
    if (!targetId) return
    this.openDrawer(document.querySelector(`#${targetId}`))
  }

  setAvailability() {
    const hasSoldoutItem = Array.from(this.availabilityInputs).some(input => input.value === 'false')
    const hasUnselected = !!this.querySelector('.js-bundle-item-id[data-unselected="true"]')

    if (!hasSoldoutItem && hasUnselected) {
      document.querySelector('.js-single-atc')?.setAttribute('disabled', '')
      document.querySelector('.js-sticky-atc')?.setAttribute('disabled', '')
    }

    if (hasSoldoutItem) {
      const atcButton = document.querySelector('.js-single-atc')
      const stickyAtcButton = document.querySelector('.js-sticky-atc')
      const soldoutLabel = atcButton.dataset.soldoutLabel || 'Out of stock'
      const quantityInput = document.querySelector('.js-pdp-quantity-input')
      this.setButtonDisabled(atcButton, soldoutLabel);

      if (stickyAtcButton) {
        this.setButtonDisabled(stickyAtcButton, soldoutLabel);
      }
      quantityInput?.setAttribute('disabled', '')
    }
  }

  setButtonDisabled(btnElement, label) {
    if (!btnElement) return;
    btnElement.setAttribute('disabled', '')
    btnElement.innerHTML = label
    btnElement.style.background = 'var(--hh-color-grey, #595959)';
    btnElement.style.cursor = 'default';
    btnElement.style.textAlign = 'left';
  }

  render() {
    return html`<slot></slot>`
  }

  static styles = css``
}

customElements.define('hh-bundle-items-picker', HHBundleItemsPicker)
